import React from "react";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Dropdown, MenuProps, Space, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClone,
  faEdit,
  faEllipsis,
  faFileExport,
  faIdCard,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateAdapter, updateOpenModalDeleteAdapter } from "../../../redux/adapters/adapterSlice";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// INTERFACES
import { AdaptersTableActionsInterface } from "../../../interfaces/Adapter.interface";
// INTERFACES
import { RootState } from "../../../app/store";
import downloadEntityAsBinary from "../../../utils/exportEntity";
import { formatUuidT } from "../../../flatbuffers/display";

function AdaptersTableActions({ selectedItem }: AdaptersTableActionsInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const navigate = useNavigate();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const handleOpenModalDelete = () => {
    dispatch(updateAdapter(selectedItem));
    dispatch(updateOpenModalDeleteAdapter(true));
  };

  const handleNavigateDuplicateAdapter = () => {
    navigate(`/edge-management/adapters/duplicate/${formatUuidT(selectedItem.id)}`);
  };

  const handleNavigateEditAdapter = () => {
    navigate(`/edge-management/adapters/edit/${formatUuidT(selectedItem.id)}`);
  };

  const handleGetId = async () => {
    if (!selectedItem.id) return;
    const id = formatUuidT(selectedItem.id);
    await navigator.clipboard.writeText(id);
    message.success(t("general.copyToClipboard"));
  };

  const handleExportAdapterBinary = async () => {
    if (!globalOrganizationId) {
      message.error("No organization selected.");
      return;
    }
    try {
      await downloadEntityAsBinary(
        globalOrganizationId,
        "adapters",
        typeof selectedItem.name === "string" ? selectedItem.name : undefined,
        formatUuidT(selectedItem.id),
      );
    } catch (err) {
      message.error(
        `Failed to export adapter: ${err instanceof Error ? err.message : "unknown error"}`,
      );
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const GET_ID = {
      label: <TableActionsMenuItem icon={faIdCard} text="Get ID" onClick={handleGetId} />,
      key: "0",
    };

    const EDIT = hasPermission(Permission.AdaptersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faEdit}
              text={t("general.edit")}
              onClick={handleNavigateEditAdapter}
            />
          ),
          key: "1",
        }
      : null;

    const DUPLICATE = hasPermission(Permission.AdaptersCreate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faClone}
              text={t("general.duplicateAdapter")}
              onClick={handleNavigateDuplicateAdapter}
            />
          ),
          key: "2",
        }
      : null;

    const EXPORT = hasPermission(Permission.AdaptersView, permissionSet ?? [])
      ? {
          label: (
            <span>
              <FontAwesomeIcon
                className="generalStyles__info generalStyles__mrFix"
                icon={faFileExport}
              />
              {t("organizationManagement.exportDescription")}
            </span>
          ),
          key: "8",
          onClick: handleExportAdapterBinary,
        }
      : null;

    const DELETE = hasPermission(Permission.AdaptersDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("adapter.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "9",
        }
      : null;

    return [GET_ID, EDIT, DUPLICATE, EXPORT, DELETE];
  };
  const menu: MenuProps = {
    items: manageActions(),
  };
  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown
        menu={menu}
        trigger={["click"]}
        overlayStyle={{ minWidth: 280, whiteSpace: "nowrap" }}
      >
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default AdaptersTableActions;
